/**
 * DataGrid Transformers
 *
 * Utility functions extracted from visualizer/js/modules/core/dataGrid/dataGrid.component.js
 * These transform raw data and configurations into the format expected by the DataGrid React component.
 */

import ConditionalFormatting from "@viz-ui/services/quickMenu/conditionalFormat/conditionalFormattingService";
import ColumnConfig from "@viz-ui/models/tableConfig/columnConfig";

/**
 * Get conditional formatters for each field
 * Extracted from dataGrid.component.js lines 177-192
 *
 * @param {Array} fieldModels - Array of field model objects
 * @param {string} fieldFormatIdentifier - Identifier for field formatting
 * @returns {Object} - Map of field names to their formatters
 */
export function getFormatters(fieldModels, fieldFormatIdentifier) {
  const result = {};

  (fieldModels || []).forEach(fieldModel => {
    const fieldName = fieldModel.name();
    result[fieldName] = {
      textColor: ConditionalFormatting.getTextColorFormatter(fieldName, fieldFormatIdentifier),
      backgroundColor: ConditionalFormatting.getBackgroundColorFormatter(fieldName, fieldFormatIdentifier),
      iconColor: ConditionalFormatting.getIconColorFormatter(fieldName, fieldFormatIdentifier),
      iconName: ConditionalFormatting.getIconNameFormatter(fieldName, fieldFormatIdentifier),
    };
  });

  return result;
}

/**
 * Transform field models to a lookup map
 * Extracted from dataGrid.component.js lines 198-206
 *
 * @param {Array} fieldModels - Array of field model objects
 * @returns {Object} - Map of field names to field model data
 */
export function transformFieldModels(fieldModels) {
  const newFieldModels = {};

  (fieldModels || []).forEach(fieldModel => {
    newFieldModels[fieldModel.name()] = {
      ...fieldModel._data,
    };
  });

  return newFieldModels;
}

/**
 * Apply a specific formatter to a value
 * Extracted from dataGrid.component.js lines 194-196
 *
 * @param {Object} fieldFormatters - Formatter functions for a field
 * @param {string} formatterName - Name of the formatter (textColor, backgroundColor, etc.)
 * @param {*} value - Value to format
 * @returns {*} - Formatted result or undefined
 */
export function applyFormatter(fieldFormatters, formatterName, value) {
  return fieldFormatters && fieldFormatters[formatterName] && fieldFormatters[formatterName](value);
}

/**
 * Parse file names string into file path objects
 * Extracted from dataGrid.component.js lines 208-215
 *
 * @param {string} fileNames - Comma-separated string of file names (format: "id-filename")
 * @param {Function} getAttachmentUrl - Function to get attachment URL by id
 * @returns {Array} - Array of file objects with path and title
 */
export function getFilePaths(fileNames, getAttachmentUrl) {
  if (!fileNames || !getAttachmentUrl) return [];

  const files = fileNames.split(",");
  return files.map(file => {
    const id = parseInt(file, 10);
    const filename = file.replace(/^[0-9]+-/, "");
    return { path: getAttachmentUrl(id), title: filename };
  });
}

/**
 * Transform raw row data for the DataGrid component
 * Extracted from dataGrid.component.js lines 121-169
 *
 * Applies conditional formatting (textColor, backgroundColor, iconColor, iconName)
 * and handles file attachment fields.
 *
 * @param {Array} rawRows - Raw row data from API
 * @param {Array} fieldModels - Array of field model objects
 * @param {Object} formatters - Formatters from getFormatters()
 * @param {Function} getAttachmentUrl - Function to get attachment URL by id
 * @returns {Array} - Transformed rows with formatting applied
 */
export function transformRowsForDataGrid(rawRows, fieldModels, formatters, getAttachmentUrl) {
  if (!rawRows || !rawRows.length) {
    return rawRows;
  }

  const transformedFieldModels = transformFieldModels(fieldModels);

  return rawRows.map(rawRow => {
    const transformedRow = {};

    for (const fieldName in rawRow) {
      if (Object.prototype.hasOwnProperty.call(rawRow, fieldName)) {
        const fieldModel = transformedFieldModels[fieldName];

        if (fieldModel) {
          const fieldFormatters = formatters[fieldName];
          const value = rawRow[fieldName];

          transformedRow[fieldName] = {
            value: value,
            textColor: applyFormatter(fieldFormatters, "textColor", value),
            backgroundColor: applyFormatter(fieldFormatters, "backgroundColor", value),
            iconColor: applyFormatter(fieldFormatters, "iconColor", value),
            iconName: applyFormatter(fieldFormatters, "iconName", value),
          };

          // Handle file and digital signature fields
          if (
            (fieldModel.type === "file" || fieldModel.type === "digital_signature") &&
            transformedRow[fieldName].value
          ) {
            const fileNames = transformedRow[fieldName].value;
            transformedRow[fieldName].files = getFilePaths(fileNames, getAttachmentUrl);
          }
        } else if (fieldName === "metadata.exception_id" || fieldName === "metadata.position") {
          // Metadata fields don't need formatting
          transformedRow[fieldName] = {
            value: rawRow[fieldName],
          };
        }
      }
    }

    return transformedRow;
  });
}

/**
 * Convert table config field objects to ColumnConfig models
 * Extracted from dataGrid.component.js lines 217-229
 *
 * @param {Array} fieldObjs - Array of field configuration objects
 * @returns {Array} - Array of ColumnConfig model instances
 */
export function tableConfigFieldObjsToColumnConfigs(fieldObjs) {
  if (!fieldObjs) return [];

  return fieldObjs.map(fieldObj =>
    new ColumnConfig()
      .name(fieldObj.fieldName)
      .visible(fieldObj.visible !== false)  // Default to true when undefined
      .width(fieldObj.columnWidth)
      .wordWrap(!!fieldObj.wordWrap)  // Normalize to boolean
  );
}

/**
 * Get column header offset from mouse event
 * Extracted from dataGrid.component.js lines 244-264
 *
 * @param {MouseEvent} mouseEvent - The mouse event from header click
 * @returns {Object} - Position object with left and top properties
 */
export function getColumnHeaderOffset(mouseEvent) {
  if (!mouseEvent) {
    return { left: 0, top: 0 };
  }

  if (mouseEvent.target) {
    // Try to find the closest th element
    const thElem = mouseEvent.target.closest ? mouseEvent.target.closest("th") : null;

    if (thElem) {
      const rect = thElem.getBoundingClientRect();
      return { left: rect.left + window.scrollX, top: rect.top + window.scrollY };
    }

    // Fall back to target element position
    const targetRect = mouseEvent.target.getBoundingClientRect();
    return { left: targetRect.left + window.scrollX, top: targetRect.top + window.scrollY };
  }

  // Fall back to mouse coordinates
  return { left: mouseEvent.x || mouseEvent.clientX || 0, top: mouseEvent.y || mouseEvent.clientY || 0 };
}

